import { z } from 'zod';

/**
 * M6 — Aplicaciones: el puente entre el instrumento y la gente.
 *
 * El cuestionario es de Copathy; la aplicación dice «esta VERSIÓN, a esta
 * empresa, en este momento, hasta esta fecha». Congela `versionId` a propósito:
 * si mañana se publica una v3, las aplicaciones abiertas siguen mostrando la
 * versión que su gente empezó a ver.
 */

/** La promesa de Copathy: medir antes, durante y después de cada sesión. */
export const MOMENTOS = ['antes', 'durante', 'despues'] as const;
export type Momento = (typeof MOMENTOS)[number];

export const MOMENTO: Record<Momento, string> = {
  antes: 'Antes de la sesión',
  durante: 'Durante la sesión',
  despues: 'Después de la sesión',
};

/**
 * El estado NO se guarda: se deriva de `abreEn` y `cierraEn` contra el reloj.
 * Guardarlo obligaría a un proceso que pase las aplicaciones a «cerrada» a
 * medianoche, y el día que ese proceso falle habría aplicaciones vencidas
 * recibiendo respuestas.
 */
export const ESTADOS_APLICACION = ['programada', 'abierta', 'cerrada'] as const;
export type EstadoAplicacion = (typeof ESTADOS_APLICACION)[number];

export const esquemaAplicacion = z.object({
  id: z.number().int().positive(),
  cuestionarioId: z.number().int().positive(),
  cuestionarioNombre: z.string(),
  versionId: z.number().int().positive(),
  version: z.number().int().positive(),
  empresaId: z.number().int().positive(),
  empresaNombre: z.string(),
  momento: z.enum(MOMENTOS),
  titulo: z.string().nullable(),
  abreEn: z.string(),
  cierraEn: z.string().nullable(),
  estado: z.enum(ESTADOS_APLICACION),
  preguntas: z.number().int(),
});
export type Aplicacion = z.infer<typeof esquemaAplicacion>;

export const esquemaCrearAplicacion = z.object({
  cuestionarioId: z.number().int().positive(),
  empresaId: z.number().int().positive(),
  momento: z.enum(MOMENTOS),
  titulo: z.string().trim().min(3).max(160).nullable().optional(),
  /** Sin fecha, abre ya. */
  abreEn: z.string().datetime({ offset: true }).optional(),
  cierraEn: z.string().datetime({ offset: true }).nullable().optional(),
});
export type EntradaCrearAplicacion = z.infer<typeof esquemaCrearAplicacion>;

export const esquemaEditarAplicacion = z
  .object({
    titulo: z.string().trim().min(3).max(160).nullable().optional(),
    /** `null` reabre una aplicación cerrada por fecha. */
    cierraEn: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarAplicacion = z.infer<typeof esquemaEditarAplicacion>;

/**
 * Lo que ve el TRABAJADOR: su actividad. Sin `empresaNombre` —es la suya— y sin
 * nada del aparato de administración.
 */
export const esquemaActividad = z.object({
  id: z.number().int().positive(),
  titulo: z.string(),
  momento: z.enum(MOMENTOS),
  cierraEn: z.string().nullable(),
  preguntas: z.number().int(),
  /**
   * De qué cuestionario sale. Va acá porque la app del trabajador tiene que poder
   * pedir la portada, y el trabajador NO puede consultar /api/cuestionarios: el
   * permiso está prohibido para roles de empresa (011). Sin este id, la carátula
   * sería invisible justo para quien la va a mirar.
   */
  cuestionarioId: z.number().int().positive(),
  tienePortada: z.boolean(),
});
export type Actividad = z.infer<typeof esquemaActividad>;
